/**
 * BugReportWidget — floating "Report a bug" button + modal.
 *
 * Spec: docs/superpowers/specs/2026-05-07-bug-reporting-design.md §5
 *
 * Visibility rules:
 *   - Hidden until session is non-null (only authenticated users see it).
 *   - Hidden if a feature flag disables it (currently a build-time env var:
 *     VITE_BUG_WIDGET_ENABLED. Switch to a runtime flag once the team has
 *     a feature-flag system wired up — see plan Phase H).
 *   - Wrapped in an error boundary so a widget bug never crashes the host page.
 */
import { Component, ReactNode, useCallback, useEffect, useState, useRef } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useAuth } from '../../context/AuthContext';
import {
  createBug,
  listBugs,
  recurBug,
  uploadScreenshot,
  type BugReport,
} from '../../services/bugs';

type ReportMode = 'new' | 'repeat';

const WIDGET_ENABLED =
  import.meta.env.VITE_BUG_WIDGET_ENABLED === undefined
    ? true
    : import.meta.env.VITE_BUG_WIDGET_ENABLED === 'true';

class WidgetErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err: Error) {
    // Eat the error so the host page never sees it. Log for debugging.
    // eslint-disable-next-line no-console
    console.error('[BugReportWidget] caught error', err);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

function WidgetInner() {
  const { session } = useAuth();
  const [open, setOpen]         = useState(false);
  const [mode, setMode]         = useState<ReportMode>('new');
  const [title, setTitle]       = useState('');
  const [description, setDesc]  = useState('');
  const [screenshot, setShot]   = useState<Blob | null>(null);
  const [submitting, setSub]    = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [confirmId, setConfirm] = useState<string | null>(null);
  const [myBugs, setMyBugs]         = useState<BugReport[]>([]);
  const [myBugsLoading, setMBLoad]  = useState(false);
  const [selectedBugId, setSelBug]  = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);
  const loadedMyBugsRef = useRef(false);

  const reset = useCallback(() => {
    setMode('new');
    setTitle('');
    setDesc('');
    setShot(null);
    setError(null);
    setConfirm(null);
    setSelBug('');
  }, []);

  // Lazy-load the user's own bugs the first time they switch to "repeat" mode.
  // Use a ref (not state) for the one-shot guard so the effect's own setState
  // doesn't trigger a re-run + cleanup that cancels the in-flight fetch.
  useEffect(() => {
    if (mode !== 'repeat' || loadedMyBugsRef.current) return;
    loadedMyBugsRef.current = true;
    setMBLoad(true);
    listBugs({ mine: true, limit: 100 })
      .then((d) => setMyBugs(d.bugs))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load your bugs'))
      .finally(() => setMBLoad(false));
  }, [mode]);

  const handleClose = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  const captureScreenshot = useCallback(async () => {
    try {
      // html2canvas is loaded lazily so the widget itself doesn't bloat the bundle.
      const html2canvas = (await import('html2canvas')).default;
      // Hide the bug-report widget (FAB + open dialog + backdrop) during
      // capture so admins see the page they were on, not the capture
      // form they just opened. Bug 153ebab3. We toggle visibility rather
      // than unmounting the dialog so the user's typed Title/Description
      // survive the capture.
      const widgetEls = Array.from(
        document.querySelectorAll<HTMLElement>('[data-bug-widget]'),
      );
      // Also catch the MUI Dialog backdrop, which is a sibling of the
      // Paper and so doesn't share the data attribute.
      const backdrops = Array.from(
        document.querySelectorAll<HTMLElement>('.MuiDialog-root .MuiBackdrop-root'),
      );
      const toHide = [...widgetEls, ...backdrops];
      const previous = toHide.map((el) => el.style.visibility);
      toHide.forEach((el) => (el.style.visibility = 'hidden'));
      // Give the browser one frame to apply the visibility change.
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      try {
        const canvas = await html2canvas(document.body, { useCORS: true, scale: 0.75 });
        canvas.toBlob((blob) => {
          if (blob) setShot(blob);
        }, 'image/png');
      } finally {
        toHide.forEach((el, i) => (el.style.visibility = previous[i]));
      }
    } catch (_e) {
      setError('Failed to capture screenshot. You can attach one from your computer instead.');
    }
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setShot(f);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (mode === 'new') {
      if (!title.trim() || !description.trim()) {
        setError('Title and description are required.');
        return;
      }
    } else {
      if (!selectedBugId) {
        setError('Pick which previous bug this is a repeat of.');
        return;
      }
      if (!description.trim()) {
        setError('Describe what happened this time.');
        return;
      }
    }
    setError(null);
    setSub(true);
    try {
      let screenshot_path: string | undefined;
      if (screenshot) {
        screenshot_path = await uploadScreenshot(screenshot);
      }
      if (mode === 'new') {
        const bug = await createBug({
          title:         title.trim(),
          description:   description.trim(),
          page_url:      window.location.href,
          user_agent:    navigator.userAgent,
          viewport_size: `${window.innerWidth}x${window.innerHeight}`,
          screenshot_path,
        });
        setConfirm(bug.id);
      } else {
        const bug = await recurBug(selectedBugId, {
          note: description.trim(),
          screenshot_path,
        });
        setConfirm(bug.id);
      }
      setTimeout(() => { handleClose(); }, 3000);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Submission failed.';
      setError(msg);
    } finally {
      setSub(false);
    }
  }, [mode, title, description, screenshot, selectedBugId, handleClose]);

  if (!session) return null;
  if (!WIDGET_ENABLED) return null;

  return (
    <>
      <Fab
        color="primary"
        size="medium"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
        }}
        aria-label="Report a bug"
        data-testid="bug-report-fab"
        data-bug-widget="fab"
      >
        <BugReportIcon />
      </Fab>

      <Dialog
        open={open}
        onClose={submitting ? undefined : handleClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="bug-report-dialog-title"
        PaperProps={{ 'data-bug-widget': 'dialog' } as any}
      >
        <DialogTitle id="bug-report-dialog-title">
          {mode === 'new' ? 'Report a bug' : 'Report a repeat bug'}
        </DialogTitle>
        <DialogContent>
          {confirmId ? (
            <Alert severity="success">
              {mode === 'new'
                ? <>Thanks — your report was submitted (ID: {confirmId.slice(0, 8)}…).</>
                : <>Thanks — recurrence logged on the original bug (ID: {confirmId.slice(0, 8)}…).</>}
            </Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField
                select
                label="Is this a new bug?"
                fullWidth
                value={mode}
                onChange={(e) => setMode(e.target.value as ReportMode)}
                disabled={submitting}
                data-testid="bug-mode-select"
              >
                <MenuItem value="new">New bug</MenuItem>
                <MenuItem value="repeat">A repeat of a previous bug I reported</MenuItem>
              </TextField>

              {mode === 'repeat' && (
                <TextField
                  select
                  label="Which bug?"
                  fullWidth
                  required
                  value={selectedBugId}
                  onChange={(e) => setSelBug(e.target.value)}
                  disabled={submitting || myBugsLoading}
                  helperText={
                    myBugsLoading
                      ? 'Loading your previous bugs…'
                      : myBugs.length === 0
                        ? "You haven't reported any bugs yet — switch back to \"New bug\"."
                        : 'Pick the original report this is happening again of.'
                  }
                  data-testid="bug-repeat-select"
                >
                  {myBugs.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      <Stack direction="row" gap={1} alignItems="center" sx={{ width: '100%' }}>
                        <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {b.title}
                        </Box>
                        <Chip size="small" label={b.status.replace('_', ' ')} variant="outlined" />
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {mode === 'new' && (
                <TextField
                  label="Short title"
                  fullWidth
                  required
                  inputProps={{ maxLength: 300 }}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={submitting}
                  data-testid="bug-title-input"
                />
              )}

              <TextField
                label={mode === 'new' ? 'What happened?' : 'What happened this time?'}
                fullWidth
                required
                multiline
                minRows={4}
                inputProps={{ maxLength: 10_000 }}
                value={description}
                onChange={(e) => setDesc(e.target.value)}
                disabled={submitting}
                data-testid="bug-description-input"
              />

              <Box display="flex" gap={1} alignItems="center">
                <Button
                  variant="outlined"
                  onClick={captureScreenshot}
                  disabled={submitting}
                  data-testid="bug-capture-screenshot"
                >
                  Capture screenshot
                </Button>
                <Button
                  variant="text"
                  component="label"
                  disabled={submitting}
                >
                  Attach file
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    hidden
                    ref={fileRef}
                    onChange={handleFileChange}
                  />
                </Button>
                {screenshot && (
                  <Typography variant="caption" sx={{ ml: 1 }}>
                    Screenshot ready ({Math.round(screenshot.size / 1024)} KB)
                  </Typography>
                )}
              </Box>

              {error && <Alert severity="error">{error}</Alert>}
            </Box>
          )}
        </DialogContent>

        {!confirmId && (
          <DialogActions>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="bug-submit"
            >
              {submitting ? <CircularProgress size={20} /> : 'Submit'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}

export function BugReportWidget() {
  return (
    <WidgetErrorBoundary>
      <WidgetInner />
    </WidgetErrorBoundary>
  );
}

export default BugReportWidget;
